import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { db } from '../../firebase';
import { collection, getDocs, doc, getDoc, addDoc, deleteDoc } from 'firebase/firestore';

const FirestoreDiagnostic = () => {
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDiagnostics = async () => {
    setLoading(true);
    const results = {
      timestamp: new Date().toISOString(),
      tests: []
    };

    try {
      // Test 1: Check Firestore Connection
      results.tests.push({ name: 'Firestore Connection', status: '✅ Connected', details: 'db object exists' });

      // Test 2: Read projects collection
      try {
        const projectsSnapshot = await getDocs(collection(db, 'projects'));
        results.tests.push({
          name: 'Read Projects Collection',
          status: '✅ Success',
          details: `Found ${projectsSnapshot.size} documents`,
          data: projectsSnapshot.docs.map(doc => ({
            id: doc.id,
            title: doc.data().title,
            showOnMainSite: doc.data().showOnMainSite,
            featured: doc.data().featured,
            tags: doc.data().tags
          }))
        });
      } catch (error) {
        results.tests.push({
          name: 'Read Projects Collection',
          status: '❌ Failed',
          details: error.message
        });
      }

      // Test 3: Read settings/tags
      try {
        const tagsDoc = await getDoc(doc(db, 'settings', 'tags'));
        results.tests.push({
          name: 'Read Settings/Tags',
          status: tagsDoc.exists() ? '✅ Success' : '⚠️ Document not found',
          details: tagsDoc.exists() ? 'Document exists' : 'Document does not exist'
        });
      } catch (error) {
        results.tests.push({
          name: 'Read Settings/Tags',
          status: '❌ Failed',
          details: error.message
        });
      }

      // Test 4: Write test (create and delete)
      try {
        const testDoc = await addDoc(collection(db, 'projects'), {
          title: 'TEST_DIAGNOSTIC_PROJECT',
          description: 'This is a test project created by diagnostic tool',
          showOnMainSite: false,
          featured: false,
          tags: ['test'],
          createdAt: new Date().toISOString()
        });
        
        // Immediately delete it
        await deleteDoc(doc(db, 'projects', testDoc.id));
        
        results.tests.push({
          name: 'Write & Delete Test',
          status: '✅ Success',
          details: `Created and deleted test document with ID: ${testDoc.id}`
        });
      } catch (error) {
        results.tests.push({
          name: 'Write & Delete Test',
          status: '❌ Failed',
          details: error.message
        });
      }

      // Test 5: Check analytics collection
      try {
        const analyticsDoc = await getDoc(doc(db, 'analytics', 'siteStats'));
        results.tests.push({
          name: 'Read Analytics',
          status: analyticsDoc.exists() ? '✅ Success' : '⚠️ Document not found',
          details: analyticsDoc.exists() ? `Views: ${analyticsDoc.data().totalViews || 0}` : 'Document does not exist'
        });
      } catch (error) {
        results.tests.push({
          name: 'Read Analytics',
          status: '❌ Failed',
          details: error.message
        });
      }

    } catch (error) {
      results.tests.push({
        name: 'General Error',
        status: '❌ Failed',
        details: error.message
      });
    }

    setDiagnosticResults(results);
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
        <h2 className="text-2xl font-bold text-white mb-4">🔍 Firestore Diagnostic Tool</h2>
        <p className="text-gray-400 mb-6">
          Run comprehensive tests to check your Firebase/Firestore connection, permissions, and data.
        </p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={runDiagnostics}
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold shadow-lg disabled:opacity-50"
        >
          {loading ? '🔄 Running Diagnostics...' : '▶️ Run Full Diagnostic'}
        </motion.button>
      </div>

      {diagnosticResults && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4">
            Diagnostic Results
            <span className="text-sm text-gray-400 ml-3">
              {new Date(diagnosticResults.timestamp).toLocaleString()}
            </span>
          </h3>

          <div className="space-y-4">
            {diagnosticResults.tests.map((test, index) => (
              <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-white">{test.name}</h4>
                  <span className="text-sm">{test.status}</span>
                </div>
                <p className="text-sm text-gray-400">{test.details}</p>
                
                {test.data && (
                  <div className="mt-3 bg-gray-900 rounded p-3 max-h-96 overflow-auto">
                    <pre className="text-xs text-gray-300">
                      {JSON.stringify(test.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-300 mb-2">💡 Troubleshooting Tips:</h4>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• If read tests fail: Check Firestore Security Rules (allow read/write for development)</li>
              <li>• If write tests fail: Verify authentication is enabled</li>
              <li>• If no projects found: Use "Load Defaults" button in Project Management</li>
              <li>• Open browser console (F12) to see detailed logs</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirestoreDiagnostic;
